'use client';

import {
  InputGroup,
  FormControl,
  HStack,
  Text,
  IconButton,
  Box,
  BoxProps,
  Wrap,
  WrapItem,
  Hide,
} from '@chakra-ui/react';
import { useUIStore } from '~/store';
import { autocompleteStyles } from '~/theme/autocomplete';
import { VscSettings } from 'react-icons/vsc';
import { MdClose } from 'react-icons/md';
import type {
  AutocompleteItem,
  AutocompleteSearchEntityType,
} from '@habity/types/autocomplete';
import { useFilters } from '~/hooks/useFilters';
import { shallow } from 'zustand/shallow';
import { useHomesStore } from '~/store/useHomesStore';
import { ROUTES } from '@habity/constants';
import { useAppStore } from '~/store/useAppStore';
import { useTranslations } from 'next-intl';
import { getMunicipalityPath } from '@habity/services/municipalities';
import { getAreaPath } from '@habity/services/areas';
import { getProjectPath } from '@habity/services/projects';
import { CustomSelect, CustomSelectProps } from './autocomplete/Select';
import { useAutocomplete } from '~/hooks/useAutocomplete';
import { useEffect } from 'react';
import { constructLocationsFromFilters } from '~/components/ui/forms/index';
import { useRouter } from 'next/navigation';
import { NestedArea, NestedMunicipality, SearchTab } from '@habity/types';
import { SearchIcon } from './autocomplete/SearchIcon';
import { LocationIconButton } from '../buttons/LocationIconButton';
import { useSearchTracking } from '~/hooks/useSearchTracking';
import { SendMessageButton } from '../buttons/SendMessageButton';
import { TestMatchingButton } from '~/components/homes/list/components/TestMatchingButton';
import { useSellStepperModal } from '~/hooks/useSellStepperModal';
import { SellStepperModal } from '~/components/ui/modals/SellStepperModal';

interface AutocompleteInputProps {
  showFiltersButton?: boolean;
  openFiltersOnFocus?: boolean;
  ignoreSearch?: boolean;
  /** Show locations from global store even when ignoreSearch is true (e.g. flyout pre-populated mode) */
  showGlobalLocations?: boolean;
  customPlaceholder?: string;
  tab?: SearchTab;
  id?: string;
  hideNavigation?: boolean;
  showLocationButton?: boolean;
  containerProps?: BoxProps;
  inputStyle?: CustomSelectProps['chakraStyles'];
  // Messaging props (for brokers SMS and speculators messages)
  showMessageButton?: boolean;
  messageQueryParams?: string;
  messageRecipientCount?: number;
  // Test matching props (admin only)
  showTestMatchingButton?: boolean;
}

export const AutocompleteInput = ({
  showFiltersButton = false,
  openFiltersOnFocus = false,
  ignoreSearch = false,
  showGlobalLocations = false,
  customPlaceholder,
  tab,
  id,
  showLocationButton,
  // hideNavigation parameter is currently unused but kept for API compatibility
  containerProps,
  inputStyle = autocompleteStyles,
  showMessageButton = false,
  messageQueryParams = '',
  messageRecipientCount = 0,
  showTestMatchingButton = false,
}: AutocompleteInputProps) => {
  const setModals = useUIStore((state) => state.setModals);
  const modals = useUIStore((state) => state.modals);
  const t = useTranslations('Inputs.Autocomplete');

  const isMobile = useAppStore((state) => state.isMobile);
  const router = useRouter();
  const setSellModalSearchText = useUIStore((state) => state.setSellModalSearchText);

  const speculatorsModal = useSellStepperModal({
    intent: 'speculators',
  });

  const { performSearch, filtersEmpty, resetFilters } = useFilters();
  const { loading } = useUIStore(
    (state) => ({ loading: state.loading }),
    shallow
  );
  const filters = useHomesStore((state) => state.filters);
  const locations = useHomesStore((state) => state.locations);
  const setLocations = useHomesStore((state) => state.setLocations);

  // Use the search tracking hook
  const { trackSearch } = useSearchTracking();

  const {
    homesLoading,
    staticHomesLoading,
    completionLoading,
    resultsLoading,
  } = loading;

  const getEntityTypesForTab = (
    tab?: SearchTab
  ): AutocompleteSearchEntityType[] | undefined => {
    switch (tab) {
      case 'homes':
        return ['area', 'municipality', 'street'];
      case 'brokers':
        return ['area', 'municipality', 'broker'];
      case 'projects':
        return ['area', 'municipality', 'project'];
      case 'municipalities':
        return ['area', 'municipality'];
      case 'areas':
        return ['area'];
      case 'speculators':
        return ['area', 'municipality', 'street_shortcut'];
      default:
        return undefined; // Use default behavior when no specific tab filtering needed
    }
  };

  const { isLoading, loadOptions, formatOptionLabel } = useAutocomplete(
    getEntityTypesForTab(tab)
  );

  const showFilters =
    showFiltersButton &&
    tab !== 'brokers' &&
    tab !== 'areas' &&
    tab !== 'municipalities' &&
    tab !== 'places' &&
    tab !== 'projects';

  const showLocation = showLocationButton && tab !== 'projects';

  const isLoadingState =
    homesLoading ||
    staticHomesLoading ||
    completionLoading ||
    resultsLoading ||
    isLoading;

  const handleChange = async (selectedOptions: AutocompleteItem[]) => {
    if (!selectedOptions?.length) {
      resetFilters(ignoreSearch);
      return;
    }

    if (!filters && !selectedOptions) return;

    const streetShortcut = selectedOptions.find(opt => opt.type === "street_shortcut");
    if (streetShortcut) {
      setSellModalSearchText(streetShortcut.name);
      speculatorsModal.onOpen();
      return;
    }

    const brokerOption = selectedOptions.find(
      (option) => option.type === 'broker'
    );

    if (brokerOption) {
      const formattedName = brokerOption.name.replace(/\s+/g, '-');
      router.push(
        `${ROUTES.SEARCH.BROKERS}/${formattedName}-${brokerOption.id}`
      );
      return;
    }

    const projectOption = selectedOptions.find(
      (option) => option.type === 'project'
    );

    if (projectOption) {
      router.push(
        getProjectPath({
          id: projectOption.id,
          name: projectOption.name,
        } as any)
      );
      return;
    }

    if (tab === 'municipalities' && selectedOptions.length > 0) {
      const selectedItem = selectedOptions[0];

      if (selectedItem.type === 'municipality') {
        router.push(
          getMunicipalityPath({
            id: selectedItem.id,
            name: selectedItem.name,
          } as NestedMunicipality)
        );
        return;
      } else if (selectedItem.type === 'area') {
        router.push(
          getAreaPath({
            id: selectedItem.id,
            name: selectedItem.name,
            municipality_id: selectedItem?.municipality?.id || null,
            municipality_name: selectedItem?.municipality?.name,
          } as NestedArea)
        );
        return;
      }
    }

    if (tab === 'areas' && selectedOptions.length > 0) {
      const selectedItem = selectedOptions[0];

      if (selectedItem.type === 'area') {
        router.push(
          getAreaPath({
            id: selectedItem.id,
            name: selectedItem.name,
            municipality_id: selectedItem?.municipality?.id || null,
            municipality_name: selectedItem?.municipality?.name,
          } as NestedArea)
        );
        return;
      }
    }

    setLocations(selectedOptions);

    const areaIds = selectedOptions
      .filter((option) => option.type === 'area')
      .map((option) => option.id);

    const municipalityIds = selectedOptions
      .filter((option) => option.type === 'municipality')
      .map((option) => option.id);

    const brokerIds = selectedOptions
      .filter((option) => option.type === 'broker')
      .map((option) => option.id);

    const streetIds = selectedOptions
      .filter((option) => option.type === 'street')
      .map((option) => option.id);

    performSearch({
      filters: {
        areaIds: areaIds.length > 0 ? areaIds : filters?.areaIds,
        municipalityIds:
          municipalityIds.length > 0
            ? municipalityIds
            : filters?.municipalityIds,
        label: selectedOptions
          .map((option) => `${option.type}:${option.id}:${option.name}`)
          .join(','),
        brokerIds: brokerIds.length > 0 ? brokerIds : filters?.brokerIds,
        streetIds: streetIds.length > 0 ? streetIds : filters?.streetIds,
      },
      ignoreSearch,
    });
  };

  useEffect(() => {
    if (ignoreSearch) return;

    const hasAreas = filters?.areaIds && filters.areaIds.length > 0;
    const hasMunicipalities =
      filters?.municipalityIds && filters.municipalityIds.length > 0;
    const hasBrokers = filters?.brokerIds && filters.brokerIds.length > 0;
    const hasStreets = filters?.streetIds && filters.streetIds.length > 0;
    const hasCoordinates = filters?.latitude && filters?.longitude;

    const hasFilters =
      filters?.label &&
      (hasAreas ||
        hasMunicipalities ||
        hasBrokers ||
        hasStreets ||
        hasCoordinates);

    if (hasFilters && !locations.length) {
      const reconstructedLocations = constructLocationsFromFilters(filters);
      if (reconstructedLocations.length > 0) {
        setLocations(reconstructedLocations);
      }
    }
  }, [filters, locations, setLocations, ignoreSearch]);

  const handleOpenFilters = () => setModals({ ...modals, searchOpen: true });

  const showSearchIcon = true; // Always show search icon since tags are displayed below input

  return (
    <>
    <Box id={id || undefined} position="relative" {...containerProps}>
      <HStack p={0} spacing="2" position="relative" zIndex="5">
        <InputGroup>
          <FormControl
            id="react-select-location-input"
            p={0}
            cursor="text"
            autoFocus={false}
          >
            {showSearchIcon && <SearchIcon isLoading={isLoadingState} />}
            <CustomSelect
              inputType="async"
              ignoreSearch={ignoreSearch}
              instanceId="location"
              isDisabled={completionLoading}
              closeMenuOnSelect={true}
              name="location"
              onKeyDown={(e: TODO) => {
                if (e.key === 'Escape' && !ignoreSearch) {
                  setModals({ ...modals, searchOpen: false });
                }
              }}
              autoFocus={false}
              onFocus={() => {
                if (openFiltersOnFocus) handleOpenFilters();
              }}
              isLoading={isLoadingState}
              placeholder={
                completionLoading
                  ? t('placeholderCompletionLoading')
                  : customPlaceholder ??
                  t(`placeholder${isMobile ? '.mobile' : ''}.${tab}`)
              }
              value={
                (ignoreSearch && !showGlobalLocations ? [] : locations).map(
                  (l?: TODO) => ({
                    ...l,
                    label: l.name,
                    type: l.type,
                    municipality: l.municipality,
                  })
                ) as TODO
              }
              onChange={handleChange}
              loadOptions={loadOptions}
              tab={tab}
              formatOptionLabel={formatOptionLabel}
              loadingMessage={() => (
                <Text fontWeight="medium" color="blackAlpha.700">
                  {t('loading')}
                </Text>
              )}
              noOptionsMessage={() => null}
              size="lg"
              tagColorScheme="brand"
              tagVariant="solid"
              selectedOptionStyle="check"
              selectedOptionColorScheme="black"
              variant="outline"
              controlShouldRenderValue={!isMobile && locations.length <= 5}
              chakraStyles={{
                ...inputStyle,
                valueContainer: (provided) => ({
                  ...provided,
                  paddingLeft: showSearchIcon ? '3rem' : '4px',
                  flexWrap: 'nowrap',
                }),
              }}
              onInputChange={(value: string) => {
                trackSearch(value);
              }}
            />
          </FormControl>
        </InputGroup>

        {showLocation && (
          <LocationIconButton
            size="lg"
            tooltipPlacement="bottom"
            tab={tab}
            aria-label="" // Overwritten inside
          />
        )}
        {showMessageButton && tab && (
          <SendMessageButton
            tab={tab}
            queryParams={messageQueryParams}
            recipientCount={messageRecipientCount}
          />
        )}
        {showTestMatchingButton && (
          <Hide below="desktop">
            <TestMatchingButton />
          </Hide>
        )}
        {showFilters && (
          <IconButton
            icon={<VscSettings />}
            variant="primary"
            backgroundColor={
              filtersEmpty() ? 'blackAlpha.900' : 'blackAlpha.900'
            } // TODO: Add orange color when filters are active here
            color="white"
            cursor="pointer"
            size="md"
            fontSize="xl"
            flexShrink="0"
            boxSize="48px"
            onClick={handleOpenFilters}
            aria-label="Filter"
            borderRadius="4px"
          />
        )}
      </HStack>

      {/* Selected locations displayed below the input on mobile, or on desktop if more than 5 */}
      {(!ignoreSearch || showGlobalLocations) &&
        (isMobile || locations.length > 5) &&
        locations.length > 0 && (
          <Wrap spacing="2" mt="2">
            {locations.map((location) => (
              <WrapItem key={`${location.type}-${location.id}`}>
                <HStack
                  spacing="2"
                  align="center"
                  px="4"
                  py="1.5"
                  rounded="full"
                  cursor="pointer"
                  color="white"
                  backgroundColor="blackAlpha.800"
                  _hover={{ backgroundColor: 'blackAlpha.900' }}
                >
                  <Text fontWeight="medium" fontSize="sm" whiteSpace="nowrap">
                    {location.name}
                  </Text>
                  <Box
                    as="button"
                    onClick={() => {
                      const newLocations = locations.filter(
                        (l) =>
                          !(l.type === location.type && l.id === location.id)
                      );
                      handleChange(newLocations);
                    }}
                    display="flex"
                    alignItems="center"
                    ml="1"
                    opacity="0.8"
                    _hover={{ opacity: 1 }}
                  >
                    <MdClose size={14} />
                  </Box>
                </HStack>
              </WrapItem>
            ))}
          </Wrap>
        )}
    </Box>
    {tab === 'speculators' && (
      <SellStepperModal {...speculatorsModal.modalProps} />
    )}
    </>
  );
};
